import React, { useEffect, useState } from "react";
import {
  getExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  getParties,
} from "../api";

export default function ExpensesView() {
  const [expenses, setExpenses] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const [form, setForm] = useState({
    expenseName: "",
    amount: "",
    description: "",
    expenseDate: "",
    partyId: "",
  });

  useEffect(() => {
    loadExpenses();
    loadParties();
  }, []);

  const loadExpenses = async () => {
    setLoading(true);
    try {
      setExpenses(await getExpenses());
    } finally {
      setLoading(false);
    }
  };

  const loadParties = async () => {
    setParties(await getParties());
  };

  const openAddModal = () => {
    setEditingExpense(null);
    setForm({
      expenseName: "",
      amount: "",
      description: "",
      expenseDate: "",
      partyId: "",
    });
    setShowModal(true);
  };

  const openEditModal = (exp) => {
    setEditingExpense(exp);
    setForm({
      expenseName: exp.expenseName,
      amount: exp.amount,
      description: exp.description,
      expenseDate: exp.expenseDate?.slice(0, 16) || "",
      partyId: exp.party?.id || "",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      amount: Number(form.amount),
      expenseDate: form.expenseDate
        ? new Date(form.expenseDate).toISOString()
        : null,
      party: form.partyId ? { id: form.partyId } : null,
    };

    editingExpense
      ? await updateExpense(editingExpense.id, payload)
      : await createExpense(payload);

    setShowModal(false);
    loadExpenses();
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleString() : "";

  return (
    <div className="so-expenses p-4 md:p-8">

      {/* Scoped SwiftOrder theme — same tokens as the rest of the POS. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-expenses {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --herb-dark: #2d4f39;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          background: var(--paper-dim);
          background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
          background-size: 24px 24px;
          font-family: var(--font-body);
          color: var(--ink);
          min-height: 100%;
        }

        .so-expenses * {
          box-sizing: border-box;
        }

        .so-exp-title {
          font-family: var(--font-display);
          font-size: 1.9rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-exp-add-btn {
          background: var(--chili);
          color: var(--paper);
          border-radius: 8px;
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
          transition: background 0.15s ease;
        }

        .so-exp-add-btn:hover {
          background: var(--chili-dark);
        }

        .so-exp-table-wrap {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 12px;
          box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.3);
          overflow: hidden;
        }

        .so-exp-thead {
          background: var(--paper-dim);
          font-family: var(--font-mono);
          font-size: 0.7rem;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--stone);
        }

        .so-exp-row {
          border-top: 1px solid var(--line);
          transition: background 0.1s ease;
        }

        .so-exp-row:hover {
          background: var(--paper-dim);
        }

        .so-exp-amount {
          font-weight: 700;
          color: var(--herb-dark);
        }

        .so-exp-edit-link {
          color: var(--chili);
          font-weight: 600;
        }

        .so-exp-edit-link:hover {
          color: var(--chili-dark);
        }

        .so-exp-delete-link {
          color: var(--stone);
          font-weight: 600;
        }

        .so-exp-delete-link:hover {
          color: var(--chili-dark);
        }

        .so-exp-card {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 10px;
          box-shadow: 0 10px 22px -18px rgba(28, 26, 23, 0.3);
        }

        .so-exp-card-name {
          font-weight: 700;
          color: var(--ink);
        }

        .so-exp-card-date {
          font-size: 0.72rem;
          color: var(--stone-light);
        }

        .so-exp-card-edit-btn {
          background: var(--paper-dim);
          color: var(--ink);
          border-radius: 8px;
          transition: background 0.15s ease;
        }

        .so-exp-card-edit-btn:hover {
          background: var(--line);
        }

        .so-exp-card-delete-btn {
          background: #fdece6;
          color: var(--chili-dark);
          border-radius: 8px;
          transition: background 0.15s ease;
        }

        .so-exp-card-delete-btn:hover {
          background: #f09595;
        }

        .so-exp-modal-overlay {
          background: rgba(28, 26, 23, 0.55);
        }

        .so-exp-modal-card {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 12px;
          box-shadow: 0 24px 50px -20px rgba(28, 26, 23, 0.45);
        }

        .so-exp-modal-title {
          font-family: var(--font-display);
          font-size: 1.5rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-exp-input {
          width: 100%;
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 10px 14px;
          background: var(--paper);
          color: var(--ink);
          outline: none;
          font-size: 0.95rem;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .so-exp-input:focus {
          border-color: var(--chili);
          box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
        }

        .so-exp-cancel-btn {
          border: 1px solid var(--line);
          color: var(--ink);
          background: #fffdf8;
          border-radius: 8px;
          transition: background 0.15s ease;
        }

        .so-exp-cancel-btn:hover {
          background: var(--paper-dim);
        }

        .so-exp-save-btn {
          background: var(--chili);
          color: var(--paper);
          border-radius: 8px;
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
          transition: background 0.15s ease;
        }

        .so-exp-save-btn:hover {
          background: var(--chili-dark);
        }
      `}</style>

      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="so-exp-title">Expenses</h2>
        <button
          onClick={openAddModal}
          className="so-exp-add-btn px-4 py-2"
        >
          + Add
        </button>
      </div>

      {/* DESKTOP TABLE */}
      <div className="so-exp-table-wrap hidden md:block">
        <table className="w-full text-sm">
          <thead className="so-exp-thead">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-right">Amount</th>
              <th className="p-3">Party</th>
              <th className="p-3">Date</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.id} className="so-exp-row">
                <td className="p-3">{e.expenseName}</td>
                <td className="p-3 text-right so-exp-amount">₹{e.amount}</td>
                <td className="p-3">{e.party?.name || "-"}</td>
                <td className="p-3">{formatDate(e.expenseDate)}</td>
                <td className="p-3">
                  <button
                    onClick={() => openEditModal(e)}
                    className="so-exp-edit-link mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteExpense(e.id).then(loadExpenses)}
                    className="so-exp-delete-link"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE CARDS */}
      <div className="md:hidden space-y-3">
        {expenses.map((e) => (
          <div key={e.id} className="so-exp-card p-4">
            <div className="flex justify-between">
              <h3 className="so-exp-card-name">{e.expenseName}</h3>
              <span className="so-exp-amount">₹{e.amount}</span>
            </div>
            <p className="so-exp-card-date mt-1">{formatDate(e.expenseDate)}</p>

            <div className="flex gap-2 mt-3">
              <button
                onClick={() => openEditModal(e)}
                className="so-exp-card-edit-btn flex-1 py-2"
              >
                Edit
              </button>
              <button
                onClick={() => deleteExpense(e.id).then(loadExpenses)}
                className="so-exp-card-delete-btn flex-1 py-2"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="so-exp-modal-overlay fixed inset-0 flex items-center justify-center px-4">
          <div className="so-exp-modal-card w-full max-w-md p-5">
            <h3 className="so-exp-modal-title mb-4">
              {editingExpense ? "Edit" : "Add"} Expense
            </h3>

            <form onSubmit={handleSave} className="space-y-3">
              <input
                placeholder="Expense Name"
                className="so-exp-input"
                value={form.expenseName}
                onChange={(e) =>
                  setForm({ ...form, expenseName: e.target.value })
                }
                required
              />
              <input
                type="number"
                placeholder="Amount"
                className="so-exp-input"
                value={form.amount}
                onChange={(e) =>
                  setForm({ ...form, amount: e.target.value })
                }
                required
              />
              <select
                className="so-exp-input"
                value={form.partyId}
                onChange={(e) =>
                  setForm({ ...form, partyId: e.target.value })
                }
              >
                <option value="">No Party</option>
                {parties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="so-exp-cancel-btn flex-1 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="so-exp-save-btn flex-1 py-2"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 









// import React, { useEffect, useState } from "react";
// import {
//   getExpenses,
//   createExpense,
//   updateExpense,
//   deleteExpense,
//   getParties,
// } from "../api";

// export default function ExpensesView() {
//   const [expenses, setExpenses] = useState([]);
//   const [parties, setParties] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [showModal, setShowModal] = useState(false);
//   const [editingExpense, setEditingExpense] = useState(null);

//   const [form, setForm] = useState({
//     expenseName: "",
//     amount: "",
//     description: "",
//     expenseDate: "",
//     partyId: "",
//   });

//   useEffect(() => {
//     loadExpenses();
//     loadParties();
//   }, []);

//   const loadExpenses = async () => {
//     setLoading(true);
//     try {
//       setExpenses(await getExpenses());
//     } finally {
//       setLoading(false);
//     }
//   };

//   const loadParties = async () => {
//     setParties(await getParties());
//   };

//   const openAddModal = () => {
//     setEditingExpense(null);
//     setForm({
//       expenseName: "",
//       amount: "",
//       description: "",
//       expenseDate: "",
//       partyId: "",
//     });
//     setShowModal(true);
//   };

//   const openEditModal = (exp) => {
//     setEditingExpense(exp);
//     setForm({
//       expenseName: exp.expenseName,
//       amount: exp.amount,
//       description: exp.description,
//       expenseDate: exp.expenseDate?.slice(0, 16) || "",
//       partyId: exp.party?.id || "",
//     });
//     setShowModal(true);
//   };

//   const handleSave = async (e) => {
//     e.preventDefault();
//     const payload = {
//       ...form,
//       amount: Number(form.amount),
//       expenseDate: form.expenseDate
//         ? new Date(form.expenseDate).toISOString()
//         : null,
//       party: form.partyId ? { id: form.partyId } : null,
//     };

//     editingExpense
//       ? await updateExpense(editingExpense.id, payload)
//       : await createExpense(payload);

//     setShowModal(false);
//     loadExpenses();
//   };

//   const formatDate = (d) =>
//     d ? new Date(d).toLocaleString() : "";

//   return (
//     <div className="p-4 md:p-8">
//       {/* HEADER */}
//       <div className="flex justify-between items-center mb-4">
//         <h2 className="text-xl md:text-3xl font-bold">Expenses</h2>
//         <button
//           onClick={openAddModal}
//           className="bg-blue-600 text-white px-4 py-2 rounded-lg"
//         >
//           + Add
//         </button>
//       </div>

//       {/* DESKTOP TABLE */}
//       <div className="hidden md:block bg-white shadow rounded">
//         <table className="w-full text-sm">
//           <thead className="bg-slate-100">
//             <tr>
//               <th className="p-3 text-left">Name</th>
//               <th className="p-3 text-right">Amount</th>
//               <th className="p-3">Party</th>
//               <th className="p-3">Date</th>
//               <th className="p-3">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {expenses.map((e) => (
//               <tr key={e.id} className="border-t">
//                 <td className="p-3">{e.expenseName}</td>
//                 <td className="p-3 text-right">₹{e.amount}</td>
//                 <td className="p-3">{e.party?.name || "-"}</td>
//                 <td className="p-3">{formatDate(e.expenseDate)}</td>
//                 <td className="p-3">
//                   <button
//                     onClick={() => openEditModal(e)}
//                     className="text-blue-600 mr-2"
//                   >
//                     Edit
//                   </button>
//                   <button
//                     onClick={() => deleteExpense(e.id).then(loadExpenses)}
//                     className="text-red-600"
//                   >
//                     Delete
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* MOBILE CARDS */}
//       <div className="md:hidden space-y-3">
//         {expenses.map((e) => (
//           <div key={e.id} className="bg-white p-4 rounded shadow">
//             <div className="flex justify-between">
//               <h3 className="font-semibold">{e.expenseName}</h3>
//               <span className="font-bold text-green-600">₹{e.amount}</span>
//             </div>
//             <p className="text-xs text-slate-500">{formatDate(e.expenseDate)}</p>

//             <div className="flex gap-2 mt-3">
//               <button
//                 onClick={() => openEditModal(e)}
//                 className="flex-1 bg-slate-100 py-2 rounded"
//               >
//                 Edit
//               </button>
//               <button
//                 onClick={() => deleteExpense(e.id).then(loadExpenses)}
//                 className="flex-1 bg-red-100 text-red-600 py-2 rounded"
//               >
//                 Delete
//               </button>
//             </div>
//           </div>
//         ))}
//       </div>

//       {/* MODAL */}
//       {showModal && (
//         <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4">
//           <div className="bg-white w-full max-w-md rounded p-4">
//             <h3 className="font-bold mb-3">
//               {editingExpense ? "Edit" : "Add"} Expense
//             </h3>

//             <form onSubmit={handleSave} className="space-y-3">
//               <input
//                 placeholder="Expense Name"
//                 className="w-full border p-2 rounded"
//                 value={form.expenseName}
//                 onChange={(e) =>
//                   setForm({ ...form, expenseName: e.target.value })
//                 }
//                 required
//               />
//               <input
//                 type="number"
//                 placeholder="Amount"
//                 className="w-full border p-2 rounded"
//                 value={form.amount}
//                 onChange={(e) =>
//                   setForm({ ...form, amount: e.target.value })
//                 }
//                 required
//               />
//               <select
//                 className="w-full border p-2 rounded"
//                 value={form.partyId}
//                 onChange={(e) =>
//                   setForm({ ...form, partyId: e.target.value })
//                 }
//               >
//                 <option value="">No Party</option>
//                 {parties.map((p) => (
//                   <option key={p.id} value={p.id}>
//                     {p.name}
//                   </option>
//                 ))}
//               </select>

//               <div className="flex gap-2">
//                 <button
//                   type="button"
//                   onClick={() => setShowModal(false)}
//                   className="flex-1 border py-2 rounded"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   className="flex-1 bg-blue-600 text-white py-2 rounded"
//                 >
//                   Save
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

